import { Component, HostListener, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { HeaderComponent } from './shared/components/header/header.component';
import { LANGS } from './shared/constants/lang.constants';
import { ChatbotComponent } from './pages/chatbot/chatbot.component';
import { filter, take } from 'rxjs';
import { LoggerService } from './shared/services/logger.service';
import { MatDialog } from '@angular/material/dialog';
import { OtpDialogModel } from './shared/interfaces/dialogs/otp-dialog.model';
import { OtpDialogComponent } from './shared/components/dialogs/otp-dialog/otp-dialog.component';
import { ForgotPasswordDialogComponent } from './shared/components/dialogs/forgot-password-dialog/forgot-password-dialog.component';
import { SingleActionDialogComponent } from './shared/components/dialogs/single-action-dialog/single-action-dialog.component';
import { DuoActionDialogComponent } from './shared/components/dialogs/duo-action-dialog/duo-action-dialog.component';
import { BmiDialogComponent } from './shared/components/dialogs/bmi-dialog/bmi-dialog.component';
import { SharedStorageService } from './shared/services/shared-storage.service';
import { AuthService } from './shared/services/auth.service';
import { IdleTimeoutService } from './shared/services/idle-timeout.service';
import { NewHeaderComponent } from "./shared/components/new-header/new-header.component";
import { VersionCheckService } from './shared/services/version-check.service';


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, ChatbotComponent, NewHeaderComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  hideHeader = false;
  translationsReady = false;

  constructor(
    private dialog: MatDialog,
    private translate: TranslateService,
    private router: Router,
    private logger: LoggerService,
    private sharedStorageService: SharedStorageService,
    private authService: AuthService,
    private idleTimeoutService: IdleTimeoutService,
    private versionCheckService: VersionCheckService

  ) {
    // 檢查當前語言是否已經載入，避免在 APP_INITIALIZER 中載入失敗導致應用程式死當
    if (this.translate.currentLang) {
      const testValue = this.translate.instant('header.my-healthProfile');
      if (testValue && testValue !== 'header.my-healthProfile') {
        console.log('AppComponent: 翻譯已載入,立即顯示');
        this.translationsReady = true;
      } else {
        console.warn('AppComponent: currentLang 存在但翻譯未準備好,等待 onLangChange');
        this.translate.onLangChange.pipe(take(1)).subscribe(() => {
          console.log('AppComponent: onLangChange 觸發,翻譯已載入');
          this.translationsReady = true;
        });
      }
    } else {
      console.log('AppComponent: currentLang 不存在,等待 onLangChange');
      this.translate.onLangChange.pipe(take(1)).subscribe(() => {
        console.log('AppComponent: onLangChange 觸發,翻譯已載入');
        this.translationsReady = true;
      });
    }
    (window as any).openOtp = (data: OtpDialogModel) =>
      this.dialog.open(OtpDialogComponent, { data });

    (window as any).openForgot = (cfg: any = {}) =>
      this.dialog.open(ForgotPasswordDialogComponent, { ...cfg });

    (window as any).openSingle = (data: any = {}, cfg: any = {}) => {
      const ref = this.dialog.open(SingleActionDialogComponent, {
        data,
        ...cfg,
      });
      if (!data.action) {
        ref.componentInstance.data.action = () => ref.close();
      }
      return ref;
    };

    (window as any).openDuo = (data: any = {}, cfg: any = {}) => {
      const ref = this.dialog.open(DuoActionDialogComponent, { data, ...cfg });
      if (!data.leftButtonAction) data.leftButtonAction = () => ref.close();
      if (!data.rightButtonAction) data.rightButtonAction = () => ref.close();
      return ref;
    };

    (window as any).openBmi = (data: any = {}, cfg: any = {}) => {
      const ref = this.dialog.open(BmiDialogComponent, { data, ...cfg });
      return ref;
    };
    this.versionCheckService.init()
  }

  ngOnInit(): void {
    // 紀錄 App 啟動
    this.logger.push('► App started');

    const loginStatus = this.sharedStorageService.get('loginStatus');

    // 如果已登入，則開始監控閒置時間
    if (loginStatus === 'memberLogin') {
      this.idleTimeoutService.startWatching();
    }

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        // 檢查當前路由是否應該隱藏 header
        const routeData =
          this.router.routerState.root.firstChild?.snapshot.data;
        this.hideHeader = routeData?.['hideHeader'] === true;
      });
  }

  ngOnDestroy() {
    this.logger.push('◄ App destroyed');
  }

  // 區分重新整理與關閉分頁，透過sessionId
  // private ensureRefreshSession() {
  // //   let sessionId = sessionStorage.getItem('sessionId');
  // //   if (!sessionId) {
  // //     sessionId = crypto.randomUUID();
  // //     sessionStorage.setItem('sessionId', sessionId);
  // //   }
    
  // //   // 使用 sessionStorage 而非 localStorage 來儲存 lastSessionId
  // //   // 這樣每個分頁都有獨立的 session 記錄,不會互相干擾
  // //   const lastSessionId = sessionStorage.getItem('lastSessionId');

  // //   const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
  // //   const isPageRefresh = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
  // //   const isExternalRedirect = document.referrer && !document.referrer.includes(window.location.hostname);

  // //   console.log('Session 檢查:', {
  // //     currentSessionId: sessionId,
  // //     lastSessionId: lastSessionId,
  // //     isPageRefresh: isPageRefresh,
  // //     isExternalRedirect: isExternalRedirect,
  // //     shouldClear: !isPageRefresh && !isExternalRedirect && lastSessionId && lastSessionId !== sessionId
  // //   });

  // //   if (!isPageRefresh && !isExternalRedirect && lastSessionId && lastSessionId !== sessionId) {
  // //     console.log('檢測到新的瀏覽器 session，清除登入狀態和資料');
      
  // //     this.authService.clearToken();
  // //     this.sharedStorageService.clear();
  // //     this.sharedStorageService.remove('myReservation');
  // //     this.sharedStorageService.remove('loginStatus');
  // //     this.sharedStorageService.remove('savedQuestionnaireData');
  // //     this.sharedStorageService.remove('createMemberRequest');
      
  // //     this.clearBrowserCache();
  // //     window.location.reload();
  // //   }

  // //   // 改用 sessionStorage 儲存
  // //   sessionStorage.setItem('lastSessionId', sessionId);
  // }

  // 清除瀏覽器快取
  // private clearBrowserCache(): void {
  //   // 清除所有類型的儲存
  //   if (typeof window !== 'undefined') {
  //     // 清除 localStorage
  //     // localStorage.clear();
  //     this.sharedStorageService.clear();
      
  //     // 清除 sessionStorage
  //     sessionStorage.clear();
      
  //     // 清除 IndexedDB (如果有使用)
  //     if ('indexedDB' in window) {
  //       indexedDB.databases().then(databases => {
  //         databases.forEach(db => {
  //           if (db.name) indexedDB.deleteDatabase(db.name);
  //         });
  //       });
  //     }
      
  //     // 清除 Service Worker 快取 (如果有使用)
  //     if ('serviceWorker' in navigator) {
  //       navigator.serviceWorker.getRegistrations().then(registrations => {
  //         registrations.forEach(registration => registration.unregister());
  //       });
  //     }
  //   }
  // }
}
